/* @flow */
import _ from 'lodash';
import React, {PropTypes} from 'react';
import { connect } from 'react-redux';
import {
  fetchBanks,
  setSelectedBank,
  setClearingNumber,
  setClearingNumberIsValid,
  setClearingNumberSuppressError,
  setClearingNumberErrorMessage,
  setAccountNumber,
  setAccountNumberIsValid,
  setAccountNumberSuppressError,
  setAccountNumberErrorMessage,
  identifyBank,
  syncBankInfoFromUser
} from 'redux/modules/bank-info';
import SSNValidator from 'validators/ssnvalidator';
import { proxyCounterpart } from '../../../scripts/proxyCounterpart';
import SelectBank from './select-bank';
import ClearingNumberField from './clearing-number-field';
import AccountNumberField from './account-number-field';

class BankInfoForm extends React.Component {

  static propTypes = {
    bankInfo: PropTypes.object.isRequired,
    isValid: PropTypes.bool.isRequired,

    fetchBanks: PropTypes.func.isRequired,
    setSelectedBank: PropTypes.func.isRequired,

    setClearingNumber: PropTypes.func.isRequired,
    setClearingNumberIsValid: PropTypes.func.isRequired,
    setClearingNumberSuppressError: PropTypes.func.isRequired,
    setClearingNumberErrorMessage: PropTypes.func.isRequired,

    setAccountNumber: PropTypes.func.isRequired,
    setAccountNumberIsValid: PropTypes.func.isRequired,
    setAccountNumberSuppressError: PropTypes.func.isRequired,
    setAccountNumberErrorMessage: PropTypes.func.isRequired,

    syncBankInfo: PropTypes.bool,
    syncBankInfoFromUser: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);
    this.changedBank = this.changedBank.bind(this);

    this.changedClearingNumber = this.changedClearingNumber.bind(this);
    this.blurredClearingNumber = this.blurredClearingNumber.bind(this);
    this.validateClearingNumber = this.validateClearingNumber.bind(this);

    this.changedAccountNumber = this.changedAccountNumber.bind(this);
    this.blurredAccountNumber = this.blurredAccountNumber.bind(this);
    this.validateAccountNumber = this.validateAccountNumber.bind(this);
  }

  componentWillMount () {
    this.props.fetchBanks();

    if (this.props.syncBankInfo) {
      this.props.syncBankInfoFromUser();
    }

    this.props.setClearingNumberIsValid(this.validateClearingNumber(this.props));
    this.props.setAccountNumberIsValid(this.validateAccountNumber(this.props));
  }

  componentWillReceiveProps (nextProps) {
    if (this.props.bankInfo.clearingNumber.value !== nextProps.bankInfo.clearingNumber.value) {
      this.props.setClearingNumberIsValid(this.validateClearingNumber(nextProps));

      if (nextProps.bankInfo.selectedBank === 'OTHER') {
        const identifiedBank = identifyBank(nextProps.bankInfo.clearingNumber.value, nextProps.bankInfo.banks);

        if (identifiedBank !== 'INITIAL') {
          this.props.setSelectedBank(identifiedBank);
        }
      }
    }

    if (this.props.bankInfo.accountNumber.value !== nextProps.bankInfo.accountNumber.value) {
      this.props.setAccountNumberIsValid(this.validateAccountNumber(nextProps));
    }

    if (this.props.bankInfo.selectedBank !== nextProps.bankInfo.selectedBank) {
      this.props.setClearingNumberIsValid(this.validateClearingNumber(nextProps));
      this.props.setAccountNumberIsValid(this.validateAccountNumber(nextProps));
    }
  }

  resetFields () {
    this.props.setClearingNumber('');
    this.props.setClearingNumberSuppressError(true);
    this.props.setAccountNumber('');
    this.props.setAccountNumberSuppressError(true);
  }

  changedBank (value) {
    this.resetFields();
    this.props.setSelectedBank(value);
  }

  changedClearingNumber (value) {
    this.props.setClearingNumber(value);
  }

  blurredClearingNumber (value) {
    this.props.setClearingNumberIsValid(this.validateClearingNumber(this.props));
    this.props.setClearingNumberSuppressError(false);
  }

  validateClearingNumber (props) {
    const value = props.bankInfo.clearingNumber.value;
    let isValid = false;
    const errorMessage = proxyCounterpart('CLEARING_NR_ERROR_' + props.bankInfo.selectedBank);
    this.props.setClearingNumberErrorMessage(errorMessage);

    switch (props.bankInfo.selectedBank) {
      case 'HANDELSBANKEN':
        isValid = new RegExp('^6[\\d]{3}$').test(value);
        break;
      case 'NORDEA_PERSON':
        isValid = new RegExp('^3300$').test(value);
        break;
      case 'NORDEA_KONTO':
        isValid = new RegExp('^[\\d]{4}$').test(value);
        break;
      case 'SWEDBANK':
        const allowFifth = value.startsWith('8');
        if (allowFifth) {
          isValid = new RegExp('^8[\\d]{4}$').test(value);
        } else {
          isValid = new RegExp('^7[\\d]{3}$').test(value);
        }
        break;
      case 'SEB':
        isValid = new RegExp('^[\\d]{4}$').test(value);
        break;
      case 'IBAN':
        isValid = new RegExp('^([a-zA-Z]){4}([a-zA-Z]){2}([0-9a-zA-Z]){2}([0-9a-zA-Z]{3})?$').test(value);
        break;
      case 'OTHER':
        isValid = new RegExp('^[\\d]{4}$').test(value);
        break;
      default:
        isValid = false;
    }

    // Lets see, if the identified bank matches the selected one.
    if (isValid && props.bankInfo.selectedBank !== 'IBAN') {
      const identifiedBank = identifyBank(value, this.props.bankInfo.banks);
      isValid = props.bankInfo.selectedBank === identifiedBank;
    }

    return isValid;
  }

  changedAccountNumber (value) {
    this.props.setAccountNumber(value);
  }

  blurredAccountNumber (value) {
    this.props.setAccountNumberIsValid(this.validateAccountNumber(this.props));
    this.props.setAccountNumberSuppressError(false);
  }

  validateAccountNumber (props) {
    const value = props.bankInfo.accountNumber.value;
    const clearingNumber = props.bankInfo.clearingNumber.value;
    let isValid = false;
    const errorMessage = proxyCounterpart('ACCOUNT_NR_ERROR_' + props.bankInfo.selectedBank);
    this.props.setAccountNumberErrorMessage(errorMessage);

    switch (props.bankInfo.selectedBank) {
      case 'HANDELSBANKEN':
        isValid = new RegExp('^[\\d]{9}$').test(value);
        break;
      case 'NORDEA_PERSON':
        isValid = SSNValidator.validateSSN(value) || false;
        if (isValid) {
          isValid = new RegExp('^[\\d]{10}$').test(value);
        }
        break;
      case 'NORDEA_KONTO':
        isValid = new RegExp('^[\\d]{7}$').test(value);
        // Allow 10 digits for certain clearing numbers
        if (!isValid &&
          _.inRange(clearingNumber, 9500, 9547 + 1) ||
          _.inRange(clearingNumber, 9960, 9969 + 1)) {
          isValid = new RegExp('^[\\d]{10}$').test(value);
        }
        break;
      case 'SWEDBANK':
        const allowFifth = clearingNumber.startsWith('8');
        if (allowFifth) {
          isValid = new RegExp('^[\\d]{7}([\\d]{1,3})?$').test(value);
        } else {
          isValid = new RegExp('^[\\d]{7}$').test(value);
        }
        break;
      case 'SEB':
        isValid = new RegExp('^[\\d]{7}$').test(value);
        break;
      case 'IBAN':
        isValid = new RegExp('^[A-Z][A-Z][A-Z\\d]*$').test(value);
        break;
      case 'OTHER':
        // Require 10 digits for certain clearing numbers
        if (_.inRange(clearingNumber, 9300, 9349 + 1) ||
          _.inRange(clearingNumber, 9570, 9579 + 1)) {
          isValid = new RegExp('^[\\d]{10}$').test(value);
        } else {
          isValid = new RegExp('^[\\d]{7}$').test(value);
        }
        break;
      default:
        isValid = false;
    }

    return isValid;
  }

  render () {
    return (<div className="bank-form">
      <SelectBank
        value={this.props.bankInfo.selectedBank}
        changed={this.changedBank}
      />

      <div className='form-row'>
        <ClearingNumberField
          value={this.props.bankInfo.clearingNumber.value}
          isValid={this.props.bankInfo.clearingNumber.isValid}
          suppressError={this.props.bankInfo.clearingNumber.suppressError}
          errorMessage={this.props.bankInfo.clearingNumber.errorMessage}
          selectedBank={this.props.bankInfo.selectedBank}
          changed={this.changedClearingNumber}
          blurred={this.blurredClearingNumber}
        />

        <AccountNumberField
          value={this.props.bankInfo.accountNumber.value}
          isValid={this.props.bankInfo.accountNumber.isValid}
          suppressError={this.props.bankInfo.accountNumber.suppressError}
          errorMessage={this.props.bankInfo.accountNumber.errorMessage}
          selectedBank={this.props.bankInfo.selectedBank}
          changed={this.changedAccountNumber}
          blurred={this.blurredAccountNumber}
        />
      </div>
    </div>);
  }
}

const mapStateToProps = ({bankInfo}) => ({
  bankInfo,
  isValid: bankInfo.clearingNumber.isValid && bankInfo.accountNumber.isValid
});

const mapDispatchToProps = {
  fetchBanks,
  setSelectedBank,
  setClearingNumber,
  setClearingNumberIsValid,
  setClearingNumberSuppressError,
  setClearingNumberErrorMessage,
  setAccountNumber,
  setAccountNumberIsValid,
  setAccountNumberSuppressError,
  setAccountNumberErrorMessage,
  syncBankInfoFromUser
};

export default connect(mapStateToProps, mapDispatchToProps)(BankInfoForm);



/** WEBPACK FOOTER **
 ** ./src/views/settings/salary-info/bank-info-form.js
 **/